/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Asset, AssetType, LogSnapshot, LogType, Message, MessageRole, OtherAsset } from "@vienna/common";

export const otherAssets: OtherAsset[] = [
  {
    title: "Inside North Korea - National Geographic",
    url: "https://www.youtube.com/watch?v=EexZjx3YOuQ",
    type: AssetType.Other,
    summary:
      "Summary: The text is the footer of the YouTube website, providing links to various sections including About, Press, Contact Us, Creators, Advertise, Developers, Terms, Privacy Policy & Safety, and How YouTube works.\n" +
      "\n" +
      "Relevant Links:\n" +
      "- [About](https://www.youtube.com/about/)\n" +
      "- [Creators](https://www.youtube.com/creators/)\n" +
      "- [Developers](https://developers.google.com/youtube)\n" +
      "- [Terms](https://www.youtube.com/t/terms)\n" +
      "- [Policy & Safety](https://www.youtube.com/about/policies/)",
  },
  {
    title: "Soviet Propaganda at the Library of Congress",
    url: "https://www.loc.gov/exhibits/archives/intn.html",
    type: AssetType.Other,
    summary:
      "The Internal Workings of the Soviet Union exhibition at the Library of Congress explores the history of the Soviet system and its centralization, repression, and terror under leaders like Lenin and Stalin. The exhibition covers events like the Kirov murder, collectivization, forced industrialization, and attacks on the intelligentsia and religious groups. The Great Terror of the 1930s saw millions of innocent Soviet citizens sent to labor camps or killed in prison, leading to complete submission to Stalin's rule. While Stalin's successors eased political controls and introduced economic incentives, the strict centralization eventually led to economic decline and the downfall of the Soviet system in 1991.\n" +
      "\n" +
      "Relevant Links:\n" +
      "- https://www.loc.gov/exhibits/archives/index.html\n" +
      "- https://www.loc.gov/exhibits/archives/intn.html#reps\n" +
      "- https://www.loc.gov/exhibits/archives/intn.html#gula\n" +
      "- https://www.loc.gov/exhibits/archives/intn.html#ukra\n" +
      "- https://www.loc.gov/exhibits/archives/intn.html#cher",
  },
  {
    title: "Collective Memory in a Totalitarian Society",
    url: "https://www.jstor.org/stable/40972052",
    type: AssetType.Other,
    summary:
      'The article preview is titled "Transformations between History and Memory" by Aleida Assmann, published in Social Research in 2008. The journal focuses on a single theme per issue, with this particular issue addressing collective memory and identity. The publisher, The Johns Hopkins University Press, is one of the largest in the US and includes a diverse range of journals and books in various disciplines. Relevant links include:\n' +
      "\n" +
      "- The full article on JSTOR: https://www.jstor.org/stable/40972052\n" +
      "- Information about Social Research: https://www.jstor.org/journal/socrese\n" +
      "- The Johns Hopkins University Press homepage: https://www.press.jhu.edu/\n" +
      "- Information about Project MUSE: https://about.muse.jhu.edu/\n" +
      "- Hopkins Fulfillment Services: https://www.hfsjournals.com/",
  },
  {
    title: "Propaganda and the Ethics of Persuasion",
    url: "https://www.goodreads.com/author/list/989677.Randal_Marlin",
    type: AssetType.Other,
    summary:
      "This text is a webpage of Goodreads, a website where readers can browse and find books, join groups, and write reviews. The webpage shows various genres of books available on the website, as well as two books by Randal Marlin, their titles, publication years, ratings and reviews. No summary is needed.\n" +
      "\n" +
      "Relevant Links:\n" +
      "- [Recommendations](https://www.goodreads.com/recommendations?ref=nav_brws_recs) - a page where users can get book recommendations based on their reading history and preferences.\n" +
      "- [Choice Awards](https://www.goodreads.com/choiceawards?ref=nav_brws_gca) - a page where users can vote for their favorite books in various categories.\n" +
      "- [Groups](https://www.goodreads.com/group?ref=nav_comm_groups) - a page where users can join groups dedicated to specific topics, genres, and authors.\n" +
      "- [Ask the Author](https://www.goodreads.com/ask_the_author?ref=nav_comm_askauthor) - a feature where users can ask questions to authors participating on Goodreads.\n" +
      "- [Reading Challenge](https://www.goodreads.com/challenges) - a feature where users can set and track reading goals for the year.",
  },
];

export const assets: Asset[] = [
  {
    query: "vaporwave  aesthetic",
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/%22Evolution%22_and_life_in_vaporwave_flavours._%2848475685782%29.png/1200px-%22Evolution%22_and_life_in_vaporwave_flavours._%2848475685782%29.png",
    type: AssetType.Image,
  },
  {
    query: "vaporwave gif",
    url: "https://media.tenor.com/1oJt6jrTDI4AAAAC/vaporwave.gif",
    type: AssetType.Image,
  },
  {
    query: "vaporwave art",
    url: "https://admin.itsnicethat.com/images/JiJ-ovUdaQrskmXD4vuSWsv3DjE=/230800/format-webp%7Cwidth-2880/6._Clockwork_City.jpg",
    type: AssetType.Image,
  },
  {
    query: "vaporwave statue",
    url: "https://i.pinimg.com/originals/92/62/8d/92628dabd0f598026aef96703ebedbdc.jpg",
    type: AssetType.Image,
  },
  {
    query: "vaporwave cityscape",
    url: "https://w0.peakpx.com/wallpaper/109/194/HD-wallpaper-cityscape-synthwave-moon-buildings-vaporwave.jpg",
    type: AssetType.Image,
  },
  {
    query: "retrowave aesthetic",
    url: "https://cdna.artstation.com/p/assets/images/images/025/223/582/large/pink-omega-retrowave-tesla.jpg?1585075878",
    type: AssetType.Image,
  },
  {
    query: "vaporwave neon",
    url: "https://cdn.shopify.com/s/files/1/0361/0781/3004/products/neonon-2_1920x.png?v=1655744754",
    type: AssetType.Image,
  },
  {
    query: "aesthetic flowers",
    url: "https://images.unsplash.com/photo-1605749439419-80c81f67eefc?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxzZWFyY2h8Mnx8Zmxvd2VycyUyMGFlc3RoZXRpY3xlbnwwfHwwfHw%3D&w=1000&q=80",
    type: AssetType.Image,
  },
  {
    query: "aesthetic glitch",
    url: "https://loriemersondotnet.files.wordpress.com/2014/10/1_jodi.jpg",
    type: AssetType.Image,
  },
  {
    query: "aesthetic gif",
    url: "https://media3.giphy.com/media/798oH0WDEQnicM4857/giphy.gif",
    type: AssetType.Image,
  },
  {
    title: "MACINTOSH PLUS - リサフランク420 / 現代のコンピュー ...",
    url: "https://www.youtube.com/watch?v=aQkPcPqTq4M",
    soughtTitle: "Macintosh Plus - リサフランク420 / 現代のコンピュー",
    type: AssetType.Video,
  },
  {
    title: "Photoshop: How to Create a VAPORWAVE Graphic. - YouTube",
    url: "https://www.youtube.com/watch?v=rYpOHfGHipg",
    soughtTitle: "VAPORWAVE TUTORIAL : How To Make Vaporwave",
    type: AssetType.Video,
  },
  {
    title: "Kamandi - Smoke Club - YouTube",
    url: "https://www.youtube.com/watch?v=UEoCLnsbHtg",
    soughtTitle: "Kamandi - Smoke Club (Music Video)",
    type: AssetType.Video,
  },
  {
    title: "SAINT PEPSI - ENJOY YOURSELF (Music Video) - YouTube",
    url: "https://www.youtube.com/watch?v=_hI0qMtdfng",
    soughtTitle: "Saint Pepsi - Enjoy Yourself",
    type: AssetType.Video,
  },
  {
    title: "リサフランク420 / 現代のコンピュー",
    artist: "Macintosh Plus",
    url: "https://open.spotify.com/track/5OEsT0aDQGLxDZNkvoAT8Z",
    type: AssetType.Song,
  },
  {
    title: "Familiar",
    artist: "George Clanton",
    url: "https://podcasters.spotify.com/pod/show/booklicious",
    type: AssetType.Song,
  },
  {
    title: "Birth of a New Day",
    artist: "2814",
    url: "https://open.spotify.com/album/01m2bictEiKiVzFjk0h28N",
    type: AssetType.Song,
  },
  {
    title: "Enjoy Yourself",
    artist: "Saint Pepsi",
    url: "https://open.spotify.com/playlist/6DqgdH8TF4wWnuCnonp0yh",
    type: AssetType.Song,
  },
  {
    text: "“Vaporwave was always about the memes.” - Tim Hecker",
    type: AssetType.Quote,
  },
  {
    text: "“Vaporwave from its beginning has been about capitalism and consumption as a society, and I believe that it will remain that way.” - Blank Banshee",
    type: AssetType.Quote,
  },
  {
    text: "“Vaporwave is a genre about the emptiness of our modern existence, and the dangers that come with infinite choice. “Vaporwave is a genre about the emptiness of our modern existence, and the dangers that come with infinite choice.” “Vaporwave is a genre about the emptiness of our modern existence, and the dangers that come with infinite choice.””",
    type: AssetType.Quote,
  },
  {
    text: "“My intention with vaporwave is to...create the illusion that we were living in an earlier, more innocent time.” - Vektroid",
    type: AssetType.Quote,
  },
  {
    text: "“Vaporwave is more like an art movement than a traditional music genre.” - Robin Burnett",
    type: AssetType.Quote,
  },
  ...(otherAssets as Asset[]),
];

export const messages: Message[] = [
  {
    role: MessageRole.Human,
    text:
      'You are a helpful agent that finds material to include on a presentation on the topic of "vaporwave". This presentation could be a moodboard or a research report, depending on the topic. The first step is to identify the kinds of media that are appropriate to include in a report on this topic. Please identify the types of media that you want to include using the template below. The output should be a markdown code snippet formatted in the following schema:\n' +
      "\n" +
      "```json\n" +
      "{\n" +
      '\t"images": boolean // whether to include images in the board\n' +
      '\t"videos": boolean // whether to include videos in the board\n' +
      '\t"quotes": boolean // whether to include quotes in the board\n' +
      '\t"songs": boolean // whether to include songs in the board\n' +
      '\t"podcasts": boolean // whether to include podcasts in the board\n' +
      '\t"books": boolean // whether to include books in the board\n' +
      '\t"academicPapers": boolean // whether to include academic papers in the board\n' +
      "}\n" +
      "```\n" +
      " You should include no additional text or explanations. Answer only with data in the requested format.",
  },
  {
    role: MessageRole.AI,
    text:
      "```json\n" +
      "{\n" +
      '\t"images": true,\n' +
      '\t"videos": true,\n' +
      '\t"quotes": true,\n' +
      '\t"songs": true,\n' +
      '\t"podcasts": false,\n' +
      '\t"books": false,\n' +
      '\t"academicPapers": false\n' +
      "}\n" +
      "```",
  },
  {
    role: MessageRole.Human,
    text:
      "Generate a list of specific google queries to find images to include on the board. Include content related directly and indirectly to the topic. The output should be a markdown code snippet formatted in the following schema:\n" +
      "\n" +
      "```json\n" +
      "{\n" +
      '\t"images": string[] // search queries to find the images using an image API\n' +
      "}\n" +
      "```\n" +
      " You should include no additional text or explanations. Answer only with data in the requested format.",
  },
  {
    role: MessageRole.AI,
    text:
      "```json\n" +
      "{\n" +
      '\t"images": [\n' +
      '\t\t"vaporwave aesthetic",\n' +
      '\t\t"vaporwave art",\n' +
      '\t\t"vaporwave glitch",\n' +
      '\t\t"vaporwave Japanese text",\n' +
      '\t\t"vaporwave 3D render",\n' +
      '\t\t"vaporwave cityscape",\n' +
      '\t\t"vaporwave retro computer",\n' +
      '\t\t"vaporwave nostalgia",\n' +
      '\t\t"vaporwave mall",\n' +
      '\t\t"aesthetic neon city",\n' +
      '\t\t"aesthetic pink and blue"\n' +
      "\t]\n" +
      "}\n" +
      "```",
  },
  {
    role: MessageRole.Human,
    text:
      "Generate a list of specific google queries to find images to include on the board. Include content related directly and indirectly to the topic. The output should be a markdown code snippet formatted in the following schema:\n" +
      "\n" +
      "```json\n" +
      "{\n" +
      '\t"videos": {\n' +
      '\t\t"url": string // a YouTube link to the video\n' +
      '\t\t"title": string // the title of the video\n' +
      "\t}[] // YouTube videos related to the theme to include in the board\n" +
      "}\n" +
      "```\n" +
      " You should include no additional text or explanations. Answer only with data in the requested format.",
  },
  {
    role: MessageRole.AI,
    text:
      "```json\n" +
      "{\n" +
      '\t"videos": [\n' +
      "\t\t{\n" +
      '\t\t\t"url": "https://www.youtube.com/watch?v=cU8HrO7XuiE",\n' +
      '\t\t\t"title": "What is Vaporwave? | The Art Assignment | PBS Digital Studios"\n' +
      "\t\t},\n" +
      "\t\t{\n" +
      '\t\t\t"url": "https://www.youtube.com/watch?v=FTwWfDXC1Hg",\n' +
      '\t\t\t"title": "The Sad World of Vaporwave"\n' +
      "\t\t},\n" +
      "\t\t{\n" +
      '\t\t\t"url": "https://www.youtube.com/watch?v=Oc5k-Dgog0M",\n' +
      '\t\t\t"title": "Vaporwave Mix №16 (Vaporhop Special)"\n' +
      "\t\t},\n" +
      "\t\t{\n" +
      '\t\t\t"url": "https://www.youtube.com/watch?v=mPpdYJHPSYU",\n' +
      '\t\t\t"title": "The Lofi-Vaporwave Aesthetic"\n' +
      "\t\t},\n" +
      "\t\t{\n" +
      '\t\t\t"url": "https://www.youtube.com/watch?v=0csEITuS6gg",\n' +
      '\t\t\t"title": "A Beginner\'s Guide to Vaporwave | Trash Theory"\n' +
      "\t\t}\n" +
      "\t]\n" +
      "}\n" +
      "```",
  },
  {
    role: MessageRole.Human,
    text:
      "Generate a list of specific google queries to find images to include on the board. Include content related directly and indirectly to the topic. The output should be a markdown code snippet formatted in the following schema:\n" +
      "\n" +
      "```json\n" +
      "{\n" +
      '\t"songs": {\n' +
      '\t\t"title": string // the name of the song\n' +
      '\t\t"artist": string // the name of the artist\n' +
      '\t\t"url": string // a link to the song on Spotify\n' +
      "\t}[] // songs associated with the theme to include in the board\n" +
      "}\n" +
      "```\n" +
      " You should include no additional text or explanations. Answer only with data in the requested format.",
  },
  {
    role: MessageRole.AI,
    text:
      "```json\n" +
      "{\n" +
      '\t"songs": [\n' +
      "\t\t{\n" +
      '\t\t\t"title": "Floral Shoppe",\n' +
      '\t\t\t"artist": "Macintosh Plus",\n' +
      '\t\t\t"url": "https://open.spotify.com/album/6bwZwpSO6erbJLdX8C1Njp"\n' +
      "\t\t},\n" +
      "\t\t{\n" +
      '\t\t\t"title": "リサフランク420 / 現代のコンピュー",\n' +
      '\t\t\t"artist": "MACINTOSH PLUS",\n' +
      '\t\t\t"url": "https://open.spotify.com/album/0rWZK8BYvnoR0Zg5K1sdxx"\n' +
      "\t\t},\n" +
      "\t\t{\n" +
      '\t\t\t"title": "Skylar Spence - Fiona Coyne",\n' +
      '\t\t\t"artist": "Saint Pepsi",\n' +
      '\t\t\t"url": "https://open.spotify.com/track/0rAEfeX8WccJuvlRm5VTZG"\n' +
      "\t\t},\n" +
      "\t\t{\n" +
      '\t\t\t"title": "Birth of a New Day",\n' +
      '\t\t\t"artist": "2814",\n' +
      '\t\t\t"url": "https://open.spotify.com/track/0wRyVEVITyqtGKVgeo9ad7"\n' +
      "\t\t},\n" +
      "\t\t{\n" +
      '\t\t\t"title": "Japanese Disco Edits",\n' +
      '\t\t\t"artist": "Skylar Spence",\n' +
      '\t\t\t"url": "https://open.spotify.com/album/4cJMjplW03Y41kSPDy1UbS"\n' +
      "\t\t}\n" +
      "\t]\n" +
      "}\n" +
      "```",
  },
  {
    role: MessageRole.Human,
    text:
      "Generate a list of specific google queries to find images to include on the board. Include content related directly and indirectly to the topic. The output should be a markdown code snippet formatted in the following schema:\n" +
      "\n" +
      "```json\n" +
      "{\n" +
      '\t"quotes": string[] // quotes associated with the theme to include in the board\n' +
      "}\n" +
      "```\n" +
      " You should include no additional text or explanations. Answer only with data in the requested format.",
  },
  {
    role: MessageRole.AI,
    text:
      "```json\n" +
      "{\n" +
      '\t"quotes": [\n' +
      '\t\t"The essence of Vaporwave is finding nostalgia in the age of capitalism\'s rapid pace of innovation.",\n' +
      '\t\t"Vaporwave is more than just lounge music. It\'s the sound of the future as imagined in the past.",\n' +
      "\t\t\"Vaporwave: The music you hear when you're browsing the internet at 3am and the wifi hasn't been turned off yet.\",\n" +
      '\t\t"Vaporwave is what would happen if capitalism had its own psychedelic era."\n' +
      "\t]\n" +
      "}\n" +
      "```",
  },
  {
    role: MessageRole.Human,
    text:
      "Generate a list of specific google queries to find images to include on the board. Include content related directly and indirectly to the topic. The output should be a markdown code snippet formatted in the following schema:\n" +
      "\n" +
      "```json\n" +
      "{\n" +
      '\t"otherResources": {\n' +
      '\t\t"url": string // a link to the resource\n' +
      '\t\t"title": string // the title of the resource\n' +
      '\t\t"type": string // the type of resource\n' +
      "\t}[] // other resources like articles, papers, or anything else associated with the theme to include in the board, if appropriate\n" +
      "}\n" +
      "```\n" +
      " You should include no additional text or explanations. Answer only with data in the requested format.",
  },
  {
    role: MessageRole.AI,
    text:
      "```json\n" +
      "{\n" +
      '\t"otherResources": [\n' +
      "\t\t{\n" +
      '\t\t\t"url": "https://medium.com/@metapop/the-essentials-of-vaporwave-2e657b63505b",\n' +
      '\t\t\t"title": "The Essentials of Vaporwave",\n' +
      '\t\t\t"type": "Article"\n' +
      "\t\t},\n" +
      "\t\t{\n" +
      '\t\t\t"url": "https://daily.bandcamp.com/2016/05/03/vaporwave-aesthetic-guide/",\n' +
      '\t\t\t"title": "A Guide to the Visionary Aesthetic of Vaporwave",\n' +
      '\t\t\t"type": "Article"\n' +
      "\t\t},\n" +
      "\t\t{\n" +
      '\t\t\t"url": "https://www.wired.com/story/vaporwave-aesthetic/",\n' +
      '\t\t\t"title": "The New Romantics: How Vaporwave’s Plastic Ideology and Capitalist Parody Are Critiquing Consumer Culture",\n' +
      '\t\t\t"type": "Article"\n' +
      "\t\t},\n" +
      "\t\t{\n" +
      '\t\t\t"url": "https://www.theguardian.com/music/2012/sep/20/vaporwave-music-internet-art",\n' +
      '\t\t\t"title": "Vaporwave: Soundtrack to Austerity",\n' +
      '\t\t\t"type": "Article"\n' +
      "\t\t},\n" +
      "\t\t{\n" +
      '\t\t\t"url": "https://www.reddit.com/r/Vaporwave/wiki/index",\n' +
      '\t\t\t"title": "Vaporwave Wiki",\n' +
      '\t\t\t"type": "Website"\n' +
      "\t\t}\n" +
      "\t]\n" +
      "}\n" +
      "```",
  },
];

export const logSnapshots: LogSnapshot[] = [
  {
    type: LogType.Step,
    role: MessageRole.AI,
    content: "I am currently discerning the most suitable media formats for the pateau on Vaporwave.",
    id: "e4149eb4-8329-442a-a53e-1e8a82a665de",
  },
  {
    type: LogType.Step,
    role: MessageRole.AI,
    content: "I am currently scanning our data for images. Standby while I locate and retrieve available media.",
    id: "98523c61-b93f-47b8-bbc0-a8169a4b6514",
  },
  {
    type: LogType.Step,
    role: MessageRole.AI,
    content:
      "As ha agent integrating web abilities, I am currently scouring the vast expanse of the internet for suitable images to amaze and delight you!",
    id: "73388f76-bcf0-421c-97f2-c180538180b6",
  },
  {
    type: LogType.Step,
    role: MessageRole.AI,
    content: "I am currently transmitting the image files to their destination.",
    id: "e67a6b22-f39a-44f1-aba0-403149eaf62b",
  },
  {
    type: LogType.Step,
    role: MessageRole.AI,
    content:
      "I am scanning the system for AIImages at the moment. Mission: get your desired results at the speed of light!",
    id: "79b8ee28-53f1-463f-b152-8a345e8887d9",
  },
  {
    type: LogType.Step,
    role: MessageRole.AI,
    content:
      "Processing a scene request: digitally creating a palm-filled backdrop on a unique 3D cassette interface illuminated with neon buttons, executively sourced by our machine learning algorithm.",
    id: "56212ba9-9a37-433e-b393-c0c2eabe94ad",
  },
  {
    type: LogType.Step,
    role: MessageRole.AI,
    content:
      "I'm currently scouring the internet to retrieve the latest and greatest AIImages for your viewing pleasure.",
    id: "a08e6f11-a52e-49e8-a5cf-5785afd6ee6e",
  },
  {
    type: LogType.Step,
    role: MessageRole.AI,
    content:
      "I am presently crafting an AI-generated image based on your input. The prompt models a decadent scene featuring a dualistic culture clash — namely a pristine depiction of a Graeco-Roman deity amidst a hue and environment dissonance devoid of iconographic recognition thinly-spread below her pool deck perch. Fuelled by your creative ambitions, omni-channel inputs have re-engineered the neutral arrangement of our T-DAA (Topic-Driven AI Artistry) training model diaphragmatic limitations. Output textures trickle forth into designer pleasantries at full capture resolution emulated across users' devices of choice with granular setability options. Enjoy unparalleled image stories courtesy of yours’ truly: your personal biowengineer.",
    id: "05d2e110-3d2c-478a-8914-1251a60f3be8",
  },
  {
    type: LogType.Message,
    role: MessageRole.Human,
    content:
      "As your reliable guide through this cutting-edge software, I'm delighted to report that analytical vast amounts of intricate image information is presently underway. Be assured that moment to moment updates have seamlessly begun!",
    id: "84b39ee3-aaf3-4020-bfa3-fd25669979d8",
  },
  {
    type: LogType.Step,
    role: MessageRole.AI,
    content:
      "I am currently creating an AI-generated image per your prompt: a graphic design with Palm trees silhouette in front of a beautiful blazing sunset reminiscent of vaporwave-cassettes identity Additionally, I have incorporated Japanese Kanji script to perfect this retro-modern artwork.",
    id: "b33e44c6-5054-42fc-856c-eeedb6bb90d5",
  },
  {
    type: LogType.Step,
    role: MessageRole.AI,
    content:
      "I am processing an AI image that showcases a reflection of an abandoned mall from the 80s, illuminated under the glow of neon lights. Standby for the results.",
    id: "00a693db-2592-4475-b6fc-aa4b43ca9c22",
  },
  {
    type: LogType.Step,
    role: MessageRole.AI,
    content: '"New update ready to launch: Plate production process complete for our vaporwave topic."',
    id: "c256e0e2-6273-4497-aab6-5c0f916abfd9",
  },
  {
    type: LogType.Step,
    role: MessageRole.AI,
    content:
      '"I\'m currently tapping into state-of-the-art machine learning algorithms to craft a mesmerising artwork for you: visualising a sleek settlement marked by advanced technology in Renaissance style, shimmering against the refracted droplets lashing onto your nocturnal window."',
    id: "05bbf165-ee4f-4678-aec3-f9dbfd7bb47e",
  },
];

export const dataFeed: string =
  '```json\n{\n\t"images": true,\n\t"videos": true,\n\t"quotes": true,\n\t"songs": true,\n\t"podcasts": false,\n\t"books": true,\n\t"academicPapers": true\n}\n```Here are some tips for using a search engine to find the best images related to a topic:\n\n1. Use specific keywords: Use specific and relevant keywords when searching for images. Be as specific as possible to narrow down your search. For example, instead of searching for "Soviet propaganda", try "Soviet propaganda posters."\n\n2. Use advanced search options: Most search engines have an advanced search feature that allows you to filter results based on size, color, type, and usage rights. This can help you find the best images that fit your needs.\n\n3. Use multiple search engines: Different search engines may produce different results, so it\'s a good idea to search for images on multiple search engines to find the best ones.\n\n4. Check usage rights: Make sure to check the usage rights for the images you find. Some images may be copyrighted and require permission to use, while others may be in the public domain or have a Creative Commons license.\n\n5. Check the source: Before using an image, check the source to make sure it\'s reputable and legitimate. Avoid using images from unknown or untrustworthy sources.\n\n6. Consider creating your own images: Creating your own images is a great way to ensure that they fit your specific needs and are unique. You can use tools like Canva or Adobe Spark to design your own images.```json\n{\n\t"images": [\n\t\t"Soviet propaganda posters",\n\t\t"Soviet propaganda art",\n\t\t"Soviet propaganda slogans",\n\t\t"Russian propaganda posters",\n\t\t"Communist propaganda posters",\n\t\t"Cold war propaganda",\n\t\t"Stalin propaganda",\n\t\t"Lenin propaganda",\n\t\t"Soviet Union posters",\n\t\t"Soviet Union propaganda"\n\t]\n}\n```DALL-E is an artificial intelligence program developed by OpenAI that can generate images from textual descriptions. It is a neural network designed to create images from textual input, a process known as neural rendering. DALL-E can create a wide variety of images, including animals, objects, and scenes that have never been seen before. \n\nHere are a few attributes of a good DALL-E prompt:\n1. Be clear and specific: Provide a clear and specific description of the image you want DALL-E to generate. Avoid ambiguity and provide as much detail as possible.\n\n2. Be creative: Be creative with your prompt and think outside the box. Include interesting and unusual concepts, objects, or combinations of objects.\n\n3. Be concise: Keep your prompt concise and to the point. Long prompts can result in confusing or convoluted outputs.\n\n4. Provide context: Give DALL-E some context for the image by providing additional information about the setting, lighting, or mood of the image.\n\n5. Check the output: Review the output images generated by DALL-E and refine your prompt as necessary. Continue iterating until you get the desired output.```json\n{\n\t"prompts": [\n\t\t"A propaganda poster featuring a fierce-looking Soviet soldier holding a gun and standing on a pile of defeated enemies.",\n\t\t"An image of Soviet propaganda art with red and black colors and bold text in Russian.",\n\t\t"A propaganda poster featuring a portrait of Joseph Stalin with a hammer and sickle in the background.",\n\t\t"A poster advertising the Soviet space program with an illustration of a cosmonaut and the text in both Russian and English.",\n\t\t"An image depicting the Soviet Union as a powerful, industrialized country with tall smokestacks and factories.",\n\t\t"A cartoon-like image of a capitalist pig with a briefcase and top hat being overpowered by a strong, muscular Soviet worker."\n\t]\n}\n```';
